/**
 * Turns a tool's JSON Schema `inputSchema` into a flat list of form fields, and turns the
 * form values back into the `arguments` object sent to MCP `tools/call`.
 *
 * We render the JSON Schema subset string/number/integer/boolean/array/object/null plus
 * `enum`, `items`, `required` and `default`. The orchestrator stores more than that
 * (`description`, nested `properties`, `minimum`/`maximum`, `minLength`/`maxLength`, `pattern`,
 * `format`, `additionalProperties` — see `orchestrator/src/modules/mcps/schemas.ts`); those
 * constraint keywords are deliberately not enforced here, so the tool itself rejects them.
 */
import type { ToolCallResult } from '../hooks/useMcpServers';

export type SchemaFieldType =
  'string' | 'number' | 'integer' | 'boolean' | 'enum' | 'array' | 'object';

export type SchemaField = {
  name: string;
  type: SchemaFieldType;
  required: boolean;
  description?: string;
  /** Original (untouched) enum members — form values hold their string form. */
  enumValues?: unknown[];
  /** Item type of an `array` field, used for per-item coercion and validation. */
  itemType?: SchemaFieldType;
  /** Enum members of an `array` field's items, when `itemType` is `enum`. */
  itemEnumValues?: unknown[];
  /**
   * Pre-filled value: the schema `default`, in the string form the inputs use. Always `''` for
   * an `array` field — an array has no single input, so its default lives in `defaultItems`.
   */
  defaultValue: string;
  /** Pre-filled item inputs of an `array` field: one entry per member of the schema `default`. */
  defaultItems?: string[];
};

/** Error message keys under `mcp.*`, resolved by the caller through `t()`. */
export type FieldErrorKey =
  | 'field_mandatory'
  | 'wrong_input_number'
  | 'wrong_input_integer'
  | 'wrong_input_json'
  | 'wrong_input_object'
  | 'wrong_input_boolean';

export type TestFormField = { value: string; items: Array<{ value: string }> };

export type TestFormValues = { fields: TestFormField[]; context: string };

/** Empty, because the server seeds the `user` and `sys-` variables a real conversation carries. */
export const DEFAULT_TEST_CONTEXT = '';

export const CONTEXT_PLACEHOLDER = `{
  "user": {
    "display_name": "John Doe",
    "email": "john@example.com"
  },
  "order_id": "12345"
}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** `type` may be a union such as `['string', 'null']` — the first usable entry wins. */
const readDeclaredType = (schema: Record<string, unknown>): string | undefined => {
  const { type } = schema;
  if (typeof type === 'string') {
    return type;
  }
  if (Array.isArray(type)) {
    return type.find((entry): entry is string => typeof entry === 'string' && entry !== 'null');
  }
  return undefined;
};

const SCALAR_TYPES: Record<string, SchemaFieldType> = {
  string: 'string',
  number: 'number',
  integer: 'integer',
  boolean: 'boolean',
  array: 'array',
  object: 'object',
};

/**
 * Objects get a JSON textarea; everything unrecognised (including `null` and a missing
 * `type`) falls back to a plain text input, which is the most forgiving option.
 */
const resolveFieldType = (schema: Record<string, unknown>): SchemaFieldType => {
  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return 'enum';
  }
  const declared = readDeclaredType(schema);
  if (declared && SCALAR_TYPES[declared]) {
    return SCALAR_TYPES[declared];
  }
  return isRecord(schema.properties) ? 'object' : 'string';
};

/**
 * Item type of an `array` field, plus the enum members when the items are an enum. The members
 * have to be carried separately from the field's own `enumValues`: an array field's `type` is
 * `array`, so without this the per-item coercion would have no members to map back to and would
 * send every item as a raw string.
 */
const resolveItems = (
  schema: Record<string, unknown>
): { itemType: SchemaFieldType; itemEnumValues?: unknown[] } => {
  const items = isRecord(schema.items) ? schema.items : undefined;
  if (!items) {
    return { itemType: 'string' };
  }
  const itemType = resolveFieldType(items);
  return itemType === 'enum' ? { itemType, itemEnumValues: items.enum as unknown[] } : { itemType };
};

/** String form of one default value, as the inputs hold it. */
const toInputValue = (raw: unknown): string => {
  if (raw === undefined || raw === null) {
    return '';
  }
  return typeof raw === 'object' ? JSON.stringify(raw, null, 2) : String(raw);
};

/**
 * Booleans only pre-fill from a declared schema `default`. Without one the select starts
 * unset, so an untouched optional flag is omitted from the payload — forcing `true` would
 * override whatever default the tool itself applies when the flag is absent.
 */
const resolveDefaultValue = (schema: Record<string, unknown>, type: SchemaFieldType): string => {
  const raw = schema.default;
  if (type === 'boolean') {
    return typeof raw === 'boolean' ? String(raw) : '';
  }
  // An array's default is spread across its item inputs instead — see `resolveDefaultItems`.
  if (type === 'array') {
    return '';
  }
  return toInputValue(raw);
};

/**
 * String form of one item input. A `boolean` item is a true/false control, so a `default` member
 * that is not a real boolean has no representation there: leave it unset rather than park a value
 * the control cannot show and `coerceScalar` would flatten to `false`. Mirrors what
 * `resolveDefaultValue` already does for a single boolean field.
 */
const toItemInputValue = (raw: unknown, itemType: SchemaFieldType): string => {
  if (itemType === 'boolean') {
    return typeof raw === 'boolean' ? String(raw) : '';
  }
  return toInputValue(raw);
};

/**
 * One string per item input of an `array` field. An array `default` pre-fills one input per
 * member — stringifying the whole array into the single starting input would send it as one
 * string. A non-array `default` (some servers declare one) pre-fills that starting input.
 */
const resolveDefaultItems = (
  schema: Record<string, unknown>,
  itemType: SchemaFieldType
): string[] => {
  const raw = schema.default;
  if (Array.isArray(raw)) {
    return raw.length > 0 ? raw.map((entry) => toItemInputValue(entry, itemType)) : [''];
  }
  return [toItemInputValue(raw, itemType)];
};

export const getSchemaFields = (inputSchema?: Record<string, unknown>): SchemaField[] => {
  const properties = isRecord(inputSchema?.properties) ? inputSchema.properties : undefined;
  if (!properties) {
    return [];
  }

  const required = new Set(
    (Array.isArray(inputSchema?.required) ? inputSchema.required : []).filter(
      (name): name is string => typeof name === 'string'
    )
  );

  return Object.entries(properties).map(([name, rawSchema]) => {
    const schema = isRecord(rawSchema) ? rawSchema : {};
    const type = resolveFieldType(schema);
    const items = type === 'array' ? resolveItems(schema) : undefined;

    return {
      name,
      type,
      required: required.has(name),
      description: typeof schema.description === 'string' ? schema.description : undefined,
      ...(type === 'enum' ? { enumValues: schema.enum as unknown[] } : {}),
      ...(items ? { ...items, defaultItems: resolveDefaultItems(schema, items.itemType) } : {}),
      defaultValue: resolveDefaultValue(schema, type),
    };
  });
};

export const getDefaultFormValues = (fields: SchemaField[]): TestFormValues => ({
  fields: fields.map((field) => ({
    value: field.defaultValue,
    // Non-array fields keep the one unused item entry that holds the positional shape together.
    items: (field.defaultItems ?? ['']).map((value) => ({ value })),
  })),
  context: DEFAULT_TEST_CONTEXT,
});

/** Plain decimal digits, so `0x1f` and `1e5` are not silently accepted as integers. */
const INTEGER_SHAPE = /^[+-]?\d+$/;

const isNumericError = (type: SchemaFieldType, raw: string): FieldErrorKey | null => {
  const trimmed = raw.trim();
  const parsed = Number(trimmed);
  if (trimmed === '' || !Number.isFinite(parsed)) {
    return type === 'integer' ? 'wrong_input_integer' : 'wrong_input_number';
  }

  if (type === 'integer') {
    // `Number.isInteger` alone would pass `0x1f`, `1e5` and magnitudes past 2^53 that round to a
    // different value than the one typed — sending those would call the tool with wrong arguments.
    if (!INTEGER_SHAPE.test(trimmed) || !Number.isSafeInteger(parsed)) {
      return 'wrong_input_integer';
    }
    return null;
  }

  // A whole number typed into a `number` argument rounds past 2^53 exactly like an `integer` one
  // does: `9007199254740993` would be sent as `9007199254740992`. Only the whole-number shape is
  // checked — decimals and exponents are approximate by nature, so there is nothing to reject.
  if (INTEGER_SHAPE.test(trimmed) && !Number.isSafeInteger(parsed)) {
    return 'wrong_input_number';
  }
  return null;
};

/** Validates one scalar value. Empty values are the caller's concern (see `validateField`). */
const validateScalar = (type: SchemaFieldType, raw: string): FieldErrorKey | null => {
  if (type === 'number' || type === 'integer') {
    return isNumericError(type, raw);
  }
  if (type === 'boolean') {
    // `coerceScalar` maps every string other than the exact `'true'` to `false`, so an unchecked
    // value is not a malformed call — it is the *opposite* call. The controls only ever produce
    // `'true'`/`'false'`, but a schema `default` can carry anything, so reject the rest here
    // rather than let `'TRUE'` or `'0'` through as `false`.
    return raw === 'true' || raw === 'false' ? null : 'wrong_input_boolean';
  }
  if (type === 'object') {
    try {
      JSON.parse(raw);
      return null;
    } catch {
      return 'wrong_input_json';
    }
  }
  return null;
};

const filledItems = (field: TestFormField): string[] =>
  field.items.map((item) => item.value).filter((value) => value.trim() !== '');

export const validateField = (field: SchemaField, value?: TestFormField): FieldErrorKey | null => {
  // A field with no form entry yet: the live schema can grow a property after `useForm` captured
  // its defaults. Treat it as empty rather than dereferencing undefined — the resolver runs
  // inside react-hook-form's promise chain, so a throw here would surface as an unhandled
  // rejection and leave the Test button permanently inert.
  if (!value) {
    return field.required ? 'field_mandatory' : null;
  }

  if (field.type === 'array') {
    const items = filledItems(value);
    if (items.length === 0) {
      return field.required ? 'field_mandatory' : null;
    }
    for (const item of items) {
      const error = validateScalar(field.itemType ?? 'string', item);
      if (error) {
        return error;
      }
    }
    return null;
  }

  if (value.value.trim() === '') {
    return field.required ? 'field_mandatory' : null;
  }

  return validateScalar(field.type, value.value);
};

/** `context` is a closed object on the far side, so an array or a scalar is rejected here. */
export const validateContext = (raw: string): FieldErrorKey | null => {
  if (raw.trim() === '') {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return 'wrong_input_json';
  }
  return isRecord(parsed) ? null : 'wrong_input_object';
};

export const buildToolContext = (raw: string): Record<string, unknown> =>
  raw.trim() === '' ? {} : (JSON.parse(raw) as Record<string, unknown>);

const coerceScalar = (type: SchemaFieldType, raw: string, enumValues?: unknown[]): unknown => {
  if (type === 'number' || type === 'integer') {
    return Number(raw);
  }
  if (type === 'boolean') {
    return raw === 'true';
  }
  if (type === 'object') {
    return JSON.parse(raw);
  }
  if (type === 'enum') {
    // Selects hold the string form of the member, so map back to the original value.
    const match = enumValues?.find((entry) => String(entry) === raw);
    return match === undefined ? raw : match;
  }
  return raw;
};

/**
 * Builds the `arguments` payload. Optional fields left empty are omitted rather than sent as
 * `""`, so servers apply their own defaults.
 */
export const buildToolArguments = (
  fields: SchemaField[],
  values: TestFormValues
): Record<string, unknown> => {
  const args: Record<string, unknown> = {};

  fields.forEach((field, index) => {
    const value = values.fields[index];
    if (!value) {
      return;
    }

    if (field.type === 'array') {
      const items = filledItems(value);
      if (items.length > 0) {
        args[field.name] = items.map((item) =>
          coerceScalar(field.itemType ?? 'string', item, field.itemEnumValues)
        );
      }
      return;
    }

    if (value.value.trim() === '') {
      return;
    }

    args[field.name] = coerceScalar(field.type, value.value, field.enumValues);
  });

  return args;
};

const collectText = (content: ToolCallResult['content']): string =>
  content
    .filter((block) => block.type === 'text' && typeof block.text === 'string')
    .map((block) => block.text as string)
    .join('\n');

/**
 * Renders a tool result as the pretty-printed JSON shown in the dark result block:
 * `structuredContent` when present, otherwise the text blocks (re-formatted when they
 * happen to hold JSON), otherwise the raw content array.
 */
export const formatToolResult = (result: ToolCallResult): string => {
  if (result.structuredContent !== undefined) {
    return JSON.stringify(result.structuredContent, null, 2);
  }

  const text = collectText(result.content);
  if (text) {
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text;
    }
  }

  return JSON.stringify(result.content, null, 2);
};

/** Human-readable description for a tool that reported its own failure (`isError: true`). */
export const getToolErrorMessage = (result: ToolCallResult): string =>
  collectText(result.content) || JSON.stringify(result.content, null, 2);
